import { useState, useRef } from 'react'
import { Upload, Camera, Loader2, CheckCircle, XCircle, AlertTriangle, Image, Leaf, Bug, Sprout } from 'lucide-react'
import './VisionDashboard.css'

function VisionDashboard({ crop }) {
    const [selectedImage, setSelectedImage] = useState(null)
    const [imagePreview, setImagePreview] = useState(null)
    const [isAnalyzing, setIsAnalyzing] = useState(false)
    const [analysisResult, setAnalysisResult] = useState(null)
    const [analysisType, setAnalysisType] = useState('planta')
    const [error, setError] = useState(null)
    const fileInputRef = useRef(null)

    const handleImageSelect = (e) => {
        const file = e.target.files[0]
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                setError('La imagen es muy grande. Máximo 10MB.')
                return
            }
            setSelectedImage(file)
            setImagePreview(URL.createObjectURL(file))
            setAnalysisResult(null)
            setError(null)
        }
    }

    const handleDrop = (e) => {
        e.preventDefault()
        const file = e.dataTransfer.files[0]
        if (file && file.type.startsWith('image/')) {
            if (file.size > 10 * 1024 * 1024) {
                setError('La imagen es muy grande. Máximo 10MB.')
                return
            }
            setSelectedImage(file)
            setImagePreview(URL.createObjectURL(file))
            setAnalysisResult(null)
            setError(null)
        }
    }

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => {
                const base64 = reader.result.split(',')[1]
                resolve(base64)
            }
            reader.onerror = error => reject(error)
        })
    }

    const analyzeImage = async () => {
        if (!selectedImage) return

        setIsAnalyzing(true)
        setError(null)

        try {
            // Get token from localStorage
            const token = localStorage.getItem('terramind_token')

            // Convert image to base64
            const imageBase64 = await convertToBase64(selectedImage)

            // Call backend API
            const response = await fetch('/api/api/gemini/analyze.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    imageBase64,
                    mimeType: selectedImage.type,
                    crop,
                    analysisType
                })
            })

            if (!response.ok) {
                throw new Error('Error al analizar la imagen')
            }

            const result = await response.json()

            if (result.error) {
                throw new Error(result.error)
            }

            // Get analysis data from nested 'analysis' object
            const analysis = result.analysis || result

            // Transform API response to our format
            setAnalysisResult({
                hasProblem: analysis.tieneProblema,
                problemName: analysis.problema,
                severity: analysis.severidad || 'ninguna',
                description: analysis.descripcion,
                actionPlan: analysis.planAccion || [],
                provider: result.provider || 'unknown',
                model: result.model || 'unknown'
            })

        } catch (err) {
            console.error('Error analyzing image:', err)

            // Fallback to mock data if API fails
            const mockResults = getMockResults()
            setAnalysisResult(mockResults[Math.floor(Math.random() * mockResults.length)])
            setError('Usando análisis de demostración. Para análisis real, verifica la conexión con la API.')
        } finally {
            setIsAnalyzing(false)
        }
    }

    const getMockResults = () => [
        {
            hasProblem: true,
            problemName: 'Mildiu (Phytophthora infestans)',
            severity: 'moderada',
            description: `Se detectan manchas oscuras irregulares en las hojas de ${crop}, características del mildiu. La enfermedad está en etapa inicial y es tratable con manejo adecuado.`,
            actionPlan: [
                'Aplicar fungicida a base de cobre (Oxicloruro de cobre) a dosis de 2-3 kg/ha',
                'Mejorar la ventilación del cultivo mediante podas sanitarias',
                'Reducir el riego para disminuir la humedad ambiente',
                'Remover y destruir las hojas severamente afectadas',
                'Monitorear cada 3 días para evaluar la evolución',
                'Considerar aplicación de Mancozeb como tratamiento complementario'
            ]
        },
        {
            hasProblem: true,
            problemName: 'Áfidos (Aphididae)',
            severity: 'leve',
            description: `Presencia de colonias de áfidos en el envés de las hojas de ${crop}. La población todavía es controlable con tratamiento oportuno.`,
            actionPlan: [
                'Aplicar insecticida biológico (Beauveria bassiana) según indicaciones del producto',
                'Liberar enemigos naturales como mariquitas o crisopas',
                'Aplicar jabón potásico al 2% como tratamiento complementario',
                'Revisar niveles de nitrógeno del suelo (exceso favorece a los áfidos)',
                'Eliminar malezas hospederas alrededor del cultivo'
            ]
        },
        {
            hasProblem: true,
            problemName: 'Deficiencia de Nitrógeno',
            severity: 'leve',
            description: `Las hojas de ${crop} muestran coloración amarillenta característica de deficiencia de nitrógeno. Afecta principalmente las hojas inferiores.`,
            actionPlan: [
                'Aplicar fertilizante nitrogenado (urea o nitrato de amonio) a 150 kg/ha',
                'Considerar aplicación foliar de nitrógeno para respuesta rápida',
                'Verificar pH del suelo (afecta disponibilidad de nutrientes)',
                'Aplicar materia orgánica para mejorar la estructura del suelo',
                'Monitorear respuesta en 7-10 días'
            ]
        },
        {
            hasProblem: false,
            problemName: null,
            severity: 'ninguna',
            description: `La planta de ${crop} muestra un excelente estado de salud. No se detectan signos de enfermedades, plagas o deficiencias nutricionales. El color, textura y desarrollo son óptimos.`,
            actionPlan: [
                'Continuar con el programa de manejo actual',
                'Mantener monitoreo preventivo semanal',
                'Asegurar nutrición balanceada según etapa fenológica',
                'Documentar las prácticas actuales como referencia de buenas prácticas'
            ]
        }
    ]

    const resetAnalysis = () => {
        setSelectedImage(null)
        setImagePreview(null)
        setAnalysisResult(null)
        setError(null)
    }

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'leve': return 'severity-low'
            case 'moderada': return 'severity-medium'
            case 'severa': return 'severity-high'
            default: return 'severity-none'
        }
    }

    return (
        <div className="vision-dashboard">
            <div className="dashboard-header">
                <div>
                    <h2>📸 Análisis de Imágenes con IA - {crop}</h2>
                    <p>Sube fotos de plantas o insectos para diagnóstico con IA</p>
                </div>
            </div>

            {/* Analysis Type Selector */}
            <div className="analysis-type-selector">
                <button
                    className={`type-btn ${analysisType === 'planta' ? 'active' : ''}`}
                    onClick={() => setAnalysisType('planta')}
                >
                    <Leaf size={18} />
                    Planta / Hoja
                </button>
                <button
                    className={`type-btn ${analysisType === 'insecto' ? 'active' : ''}`}
                    onClick={() => setAnalysisType('insecto')}
                >
                    <Bug size={18} />
                    Insecto / Plaga
                </button>
                <button
                    className={`type-btn ${analysisType === 'cultivo' ? 'active' : ''}`}
                    onClick={() => setAnalysisType('cultivo')}
                >
                    <Sprout size={18} />
                    Cultivo General
                </button>
            </div>

            <div className="vision-content">
                {/* Upload Section */}
                <div className="upload-section glass-card">
                    {!imagePreview ? (
                        <div
                            className="upload-dropzone"
                            onClick={() => fileInputRef.current?.click()}
                            onDrop={handleDrop}
                            onDragOver={(e) => e.preventDefault()}
                        >
                            <Upload size={48} />
                            <h3>Arrastra una imagen aquí</h3>
                            <p>o haz clic para seleccionar</p>
                            <span className="upload-hint">JPG, PNG hasta 10MB</span>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageSelect}
                                style={{ display: 'none' }}
                            />
                        </div>
                    ) : (
                        <div className="image-preview">
                            <img src={imagePreview} alt="Preview" />
                            <div className="preview-actions">
                                {!analysisResult && (
                                    <>
                                        <button
                                            className="btn btn-primary btn-lg"
                                            onClick={analyzeImage}
                                            disabled={isAnalyzing}
                                        >
                                            {isAnalyzing ? (
                                                <>
                                                    <Loader2 className="animate-spin" size={20} />
                                                    Analizando con IA...
                                                </>
                                            ) : (
                                                <>
                                                    <Camera size={20} />
                                                    Analizar {analysisType}
                                                </>
                                            )}
                                        </button>
                                        <button className="btn btn-secondary" onClick={resetAnalysis}>
                                            Cambiar imagen
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="error-message glass-card">
                        <AlertTriangle size={18} />
                        {error}
                    </div>
                )}

                {/* Results Section */}
                {analysisResult && (
                    <div className="results-section glass-card">
                        <div className="result-header">
                            {analysisResult.hasProblem ? (
                                <div className={`result-status problem ${getSeverityColor(analysisResult.severity)}`}>
                                    <AlertTriangle size={32} />
                                    <div>
                                        <h3>⚠️ Problema Detectado</h3>
                                        <span className={`severity-badge ${getSeverityColor(analysisResult.severity)}`}>
                                            Severidad: {analysisResult.severity}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="result-status healthy">
                                    <CheckCircle size={32} />
                                    <div>
                                        <h3>✅ Planta Saludable</h3>
                                        <span className="severity-badge severity-none">Sin problemas detectados</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {analysisResult.problemName && (
                            <div className="result-problem">
                                <h4>🔍 Problema Identificado</h4>
                                <p className="problem-name">{analysisResult.problemName}</p>
                            </div>
                        )}

                        <div className="result-description">
                            <h4>📋 Diagnóstico</h4>
                            <p>{analysisResult.description}</p>
                        </div>

                        <div className="result-actions">
                            <h4>✅ Plan de Acción Recomendado</h4>
                            <ol className="action-list">
                                {analysisResult.actionPlan.map((action, index) => (
                                    <li key={index}>{action}</li>
                                ))}
                            </ol>
                        </div>

                        <div className="result-footer">
                            <button className="btn btn-primary" onClick={resetAnalysis}>
                                <Image size={18} />
                                Analizar otra imagen
                            </button>
                            <span className="powered-by" style={{
                                background: analysisResult.provider === 'groq' ? '#f97316' : '#4285f4',
                                padding: '4px 12px',
                                borderRadius: '16px',
                                color: 'white',
                                fontSize: '12px'
                            }}>
                                🤖 {analysisResult.provider === 'groq' ? 'Gr' : 'Ge'} | {analysisResult.model}
                            </span>
                        </div>
                    </div>
                )}

                {/* Recent Analyses */}
                {!analysisResult && (
                    <div className="recent-analyses glass-card">
                        <h3>📋 Análisis Recientes</h3>
                        <div className="analysis-history">
                            <div className="history-item">
                                <span className="history-icon">🍃</span>
                                <div className="history-content">
                                    <div className="history-title">Hoja de {crop} - Saludable</div>
                                    <div className="history-desc">Sin problemas detectados</div>
                                </div>
                                <span className="history-time">Ayer</span>
                            </div>
                            <div className="history-item">
                                <span className="history-icon">🐛</span>
                                <div className="history-content">
                                    <div className="history-title">Áfidos detectados</div>
                                    <div className="history-desc">Severidad: Leve. Tratamiento aplicado.</div>
                                </div>
                                <span className="history-time">Hace 3 días</span>
                            </div>
                            <div className="history-item">
                                <span className="history-icon">🍂</span>
                                <div className="history-content">
                                    <div className="history-title">Deficiencia de Nitrógeno</div>
                                    <div className="history-desc">Fertilización correctiva realizada.</div>
                                </div>
                                <span className="history-time">Hace 1 semana</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default VisionDashboard
